/* Лист поверх страницы (APP-SPEC §2.7, BotCore.sheet): на телефоне снизу, на широком экране карточкой по центру; подложка .bscrim.
   Разметка и классы — как у прототипа (.side.bsheet, open, .bscrim.on), въезд и уход — переходы bot-core.css (opacity и transform).
   Правила: лист поверх листа не открывается — открытие закрывает другой открытый лист (кроме слоя layer); закрывают крестик, подложка, Escape. */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Entry {
  id: string;
  layer: boolean;
  close(): void;
}
const OPEN = new Map<string, Entry>();

/** Закрыть все открытые листы, кроме id (слои не трогает открытие слоя) */
function closeOthers(id: string, layer: boolean) {
  if (layer) return;
  for (const [k, e] of OPEN) if (k !== id) e.close();
  /* открытые листы прототипа (BotCore.sheet) закрываются их же крестиком: правило одно на всё приложение */
  document.querySelectorAll('.side.bsheet.open:not(.lay)').forEach(p => {
    if (p.id !== id) p.querySelector<HTMLElement>('[data-bsheet-x]')?.click();
  });
}

let escSet = false;
function installEsc() {
  if (escSet) return;
  escSet = true;
  document.addEventListener(
    'keydown',
    e => {
      if (e.key !== 'Escape' || !OPEN.size) return;
      const target = e.target instanceof Element ? e.target : document.activeElement;
      const nativeDialog = target?.closest('dialog[open]');
      // Вложенный нативный редактор обрабатывает Escape прежде лежащего под ним листа.
      if (nativeDialog && !OPEN.has(nativeDialog.id)) return;
      const L = [...OPEN.values()];
      const top = L.findLast(x => x.layer) || L[L.length - 1];
      e.preventDefault();
      e.stopImmediatePropagation();
      top.close();
    },
    true,
  );
}

interface Props {
  id: string;
  label: string;
  open: boolean;
  onClose(): void;
  tall?: boolean;
  layer?: boolean;
  children: ReactNode;
  /** Куда ставить лист: по умолчанию #bot (как у листов каркаса) */
  host?: Element | null;
  modal?: boolean;
  className?: string;
}

/* Пока открыт лист (не слой), раздел под ним недоступен с клавиатуры и для чтения с экрана — как было у нативного <dialog>:
   кнопку формы под листом согласия или «Не хватает Circle» не нажать Tab'ом. Счётчик — листов может быть несколько подряд */
let inertCount = 0;
function holdInert(on: boolean) {
  const view = document.getElementById('view');
  inertCount = Math.max(0, inertCount + (on ? 1 : -1));
  if (view) view.inert = inertCount > 0;
}

/** Лист снимают условным монтированием ({x && <Sheet …/>}): узел пропадает сразу, и переход ухода из bot-core.css не успевает сыграть.
    Перед снятием на его место ставится неактивная копия в том же виде; она теряет open/on и уходит переходом шаблона, затем удаляется */
const GHOST_MS = 320;
function leaveGhost(sheet: HTMLElement | null, scrim: HTMLElement | null) {
  if (!sheet?.isConnected || !sheet.classList.contains('open')) return;
  if (window.BotCore?.reduced?.() || document.documentElement.classList.contains('still')) return;
  const copy = (el: HTMLElement) => {
    const c = el.cloneNode(true) as HTMLElement;
    c.removeAttribute('id');
    c.setAttribute('aria-hidden', 'true');
    c.inert = true;
    c.style.pointerEvents = 'none';
    el.after(c);
    return c;
  };
  const ghost = copy(sheet);
  /* копия не помнит прокрутку: тело листа уходит с той же позиции, без прыжка к началу */
  const bodies = sheet.querySelectorAll<HTMLElement>('.cbody');
  ghost.querySelectorAll<HTMLElement>('.cbody').forEach((b, i) => (b.scrollTop = bodies[i]?.scrollTop ?? 0));
  const ghostScrim = scrim?.isConnected ? copy(scrim) : null;
  void getComputedStyle(ghost).opacity;
  ghost.classList.remove('open');
  ghostScrim?.classList.remove('on');
  setTimeout(() => {
    ghost.remove();
    ghostScrim?.remove();
  }, GHOST_MS);
}

export function Sheet({ id, label, open, onClose, tall, layer = false, children, host, modal = false, className = '' }: Props) {
  const ref = useRef<HTMLElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  /* класс open ставится кадром позже вставки: закрытый вид успевает посчитаться, и переход въезда идёт */
  const [shown, setShown] = useState(false);
  const close = useRef(onClose);
  useLayoutEffect(() => {
    close.current = onClose;
  });
  useEffect(() => installEsc(), []);
  useLayoutEffect(() => {
    if (!open) {
      OPEN.delete(id);
      return;
    }
    closeOthers(id, layer);
    OPEN.set(id, { id, layer, close: () => close.current() });
    if (ref.current) void getComputedStyle(ref.current).opacity;
    const r = requestAnimationFrame(() => setShown(true));
    return () => {
      cancelAnimationFrame(r);
      setShown(false);
    };
  }, [open, id, layer]);
  useEffect(() => () => void OPEN.delete(id), [id]);
  /* лист в оболочке шаблона: фон недоступен, фокус возвращается туда, откуда лист открыли */
  useLayoutEffect(() => {
    if (modal || !open || layer) return;
    const previous = document.activeElement as HTMLElement | null;
    const inView = !!ref.current?.closest('#view');
    if (!inView) holdInert(true);
    return () => {
      if (!inView) holdInert(false);
      if (previous?.isConnected && !previous.closest('[inert]')) previous.focus({ preventScroll: true });
    };
  }, [modal, open, layer]);
  /* снятие листа целиком: уход доигрывает копия */
  useLayoutEffect(() => {
    if (modal) return;
    const sheet = ref.current, under = scrim.current;
    return () => leaveGhost(sheet, under);
  }, [modal]);
  useLayoutEffect(() => {
    if (!modal || !open) return;
    const previous = document.activeElement as HTMLElement | null;
    const element = dialog.current;
    if (element?.showModal) element.showModal();
    else element?.setAttribute('open', '');
    return () => {
      element?.close?.();
      element?.removeAttribute('open');
      if (previous?.isConnected && !previous.closest('[inert]')) previous.focus({ preventScroll: true });
    };
  }, [modal, open]);
  const target = host || document.getElementById('bot') || document.body;
  if (modal)
    return createPortal(
      <dialog
        ref={dialog}
        id={id}
        className={`side bsheet ${className}${shown ? ' open' : ''}`}
        aria-label={label}
        aria-modal="true"
        aria-hidden={!open}
        tabIndex={-1}
        onCancel={event => {
          event.preventDefault();
          close.current();
        }}
        onClick={event => {
          if (event.target !== event.currentTarget) return;
          const box = event.currentTarget.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)
            close.current();
        }}
      >
        {children}
      </dialog>,
      target,
    );
  return createPortal(
    <>
      <div ref={scrim} className={'bscrim' + (layer ? ' lay' : '') + (shown ? ' on' : '')} id={id + 'Scrim'} onClick={() => close.current()} />
      <aside
        ref={ref}
        className={
          'side bsheet' + (className ? ` ${className}` : '') + (layer ? ' lay' : '') + (tall ? ' tall' : '') + (shown ? ' open' : '')
        }
        id={id}
        role="dialog"
        aria-label={label}
        /* скрывать от чтения с экрана нечего: класс open отвечает только за въезд, лист уже открыт */
        aria-hidden={!open}
        /* закрытый лист, оставленный в документе (#stPre), не берёт фокус */
        inert={!open}
      >
        {children}
      </aside>
    </>,
    target,
  );
}

/** Шапка листа (BotCore.shead): «назад», заголовок, подпись, крестик */
export function SheetHead({ title, sub, onBack, onClose }: { title: string; sub?: ReactNode; onBack?(): void; onClose?(): void }) {
  const ic = window.SteosUI?.icons || {};
  return (
    <div className="shead">
      {onBack && (
        <button
          className="iconb back"
          type="button"
          aria-label="Назад"
          onClick={onBack}
          dangerouslySetInnerHTML={{ __html: ic.back || '' }}
        />
      )}
      <div className="m">
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
      </div>
      {onClose && (
        <button
          className="iconb xbtn"
          type="button"
          aria-label="Закрыть"
          onClick={onClose}
          dangerouslySetInnerHTML={{ __html: ic.x18 || '' }}
        />
      )}
    </div>
  );
}
